import { rootApi } from "../../api/rootApi";

/**
 * Inventory Management API
 *
 * Required Backend Endpoints (to be implemented):
 *
 * 1. POST /tenant/ecommerce/inventory/all
 *    Headers: { slug, Authorization }
 *    Body: { product_id? } — optional filter
 *    Response: {
 *      body: [{
 *        product_size_id, product_id, product_name, article_no,
 *        color_id, color_name, hex_code,
 *        size_id, size_name,
 *        current_inventory, mrp, status ("1"=active, "0"=out_of_stock)
 *      }]
 *    }
 *
 * 2. POST /tenant/ecommerce/inventory/update
 *    Headers: { slug, Authorization }
 *    Body: {
 *      product_size_id,
 *      quantity,          — units to add/subtract/set
 *      type,              — "add" | "subtract" | "set" | "out_of_stock"
 *      note?              — optional reason/remark
 *    }
 *    Response: { body: { success: true, new_inventory: number } }
 *
 * 3. POST /tenant/ecommerce/inventory/history
 *    Headers: { slug, Authorization }
 *    Body: {
 *      product_size_id?,   — filter by specific variant
 *      product_id?,        — filter by product
 *      from_date?,         — ISO date string
 *      to_date?,           — ISO date string
 *      page?,              — default 1
 *      limit?              — default 20
 *    }
 *    Response: {
 *      body: {
 *        data: [{
 *          id, product_size_id, product_name, color_name, hex_code, size_name,
 *          previous_inventory, new_inventory, change_quantity,
 *          type,      — "add" | "subtract" | "set" | "out_of_stock"
 *          note,
 *          created_at,
 *          created_by_name
 *        }],
 *        total, page, limit
 *      }
 *    }
 *
 * Suggested DB Table — ecommerce_inventory_log:
 *   id, product_size_id (FK ecommerce_product_sizes), previous_inventory,
 *   new_inventory, change_quantity, type, note, created_by (FK users), created_at
 */

export const ecommerceInventoryApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    // Fetch all variants with current inventory (optionally filtered by product)
    fetchEcommerceInventory: builder.mutation({
      query: ({ slug, token, ...body }) => ({
        url: `/tenant/ecommerce/inventory/all`,
        method: "POST",
        headers: { slug, "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body,
      }),
    }),

    // Update inventory for a specific variant
    updateEcommerceInventory: builder.mutation({
      query: ({ slug, token, ...body }) => ({
        url: `/tenant/ecommerce/inventory/update`,
        method: "POST",
        headers: { slug, "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body,
      }),
    }),

    // Fetch inventory change history/log
    fetchEcommerceInventoryHistory: builder.mutation({
      query: ({ slug, token, ...body }) => ({
        url: `/tenant/ecommerce/inventory/history`,
        method: "POST",
        headers: { slug, "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body,
      }),
    }),
  }),
});

export const {
  useFetchEcommerceInventoryMutation,
  useUpdateEcommerceInventoryMutation,
  useFetchEcommerceInventoryHistoryMutation,
} = ecommerceInventoryApi;
